

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewTravelInvoiceModal from 'components/NewTravelInvoiceModal';
import { formatDate } from 'utils/DateTimeConversion';

const TravelInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('travel_invoices');

    useEffect(() => {
        fetchInvoices();
    }, []);

    const fetchInvoices = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/travel-invoices/');
            setInvoices(response.data);
        } catch (error) {
            console.error('Error fetching travel invoices:', error);
            setSnackbar({ open: true, message: 'Error loading travel invoices', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveInvoice = async (payload) => {
        try {
            await axiosInstance.post('/travel-invoices/', payload);
            setSnackbar({ open: true, message: 'Travel Invoice created successfully', severity: 'success' });
            fetchInvoices();
        } catch (error) {
            console.error('Error saving travel invoice:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/travel-invoice/${rowData.id}`);
    };

    const formatCurrency = (amount, currency = 'GBP') => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(amount));
    };

    const columns = [
        {
            Header: "Invoice Number",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value || `Invoice #${row.original.id}`}
                </MDTypography>
            )
        },
        {
            Header: "Order",
            accessor: "order_id",
            width: "12%",
            Cell: ({ row }) => row.original.order?.name || row.original.order_id || '-'
        },
        {
            Header: "Record Type",
            accessor: "record_type",
            width: "12%"
        },
        {
            Header: "Amount",
            accessor: "amount",
            width: "12%",
            Cell: ({ value, row }) => formatCurrency(value, row.original.currency)
        },
        {
            Header: "Currency",
            accessor: "currency",
            width: "8%"
        },
        {
            Header: "Status",
            accessor: "status",
            width: "12%"
        },
        {
            Header: "Invoice Date",
            accessor: "invoice_date",
            width: "12%",
            Cell: ({ value }) => value ? formatDate(value) : '-'
        },
        {
            Header: "Due Date",
            accessor: "due_date",
            width: "12%",
            Cell: ({ value }) => value ? formatDate(value) : '-'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "5%",
            Cell: ({ value }) => value ? formatDate(value) : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: invoices,
    }), [invoices]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Travel Invoices</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Travel Invoice
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : invoices.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                        onRowClick={handleRowClick}
                                    />
                                ) : (
                                    <EmptyState moduleName="Travel Invoice" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewTravelInvoiceModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveInvoice}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelInvoices;

